import { EventTypes } from 'app/modules/rulesAdvanced/models';
import { RtrDecision, RuleSummary } from 'app/modules/rules/models';
import { FullTagResponse } from 'app/modules/tags/responses';
import { Node } from '@xyflow/react';
import { NodeDrawerTypes } from 'app/modules/ruleFlows/components/NodeDrawer/types';
import { RemoveFlowRulePayload } from 'app/modules/ruleFlows/queries/useRemoveRuleFromFlow';

export interface CreateDraftFlowPayload {
  title: string;
  event_type: EventTypes;
  event_subtypes: string[];
  description: string;
  tag_ids: number[];
}

export enum CreateDraftFlowFormFields {
  TITLE = 'title',
  EVENT_TYPE = 'event_type',
  EVENT_SUBTYPES = 'event_subtypes',
  DESCRIPTION = 'description',
  TAG_IDS = 'tag_ids',
}

export interface AddOrEditFlowRuleFormValues {
  alertQueueId?: number;
  decision?: RtrDecision;
  flowRuleId?: number;
  generatesAlerts?: boolean;
}

export enum AddOrEditFlowRuleFormFields {
  ALERT_QUEUE_ID = 'alertQueueId',
  DECISION = 'decision',
  FLOW_RULE_ID = 'flowRuleId',
  GENERATES_ALERTS = 'generatesAlerts',
}

export interface NodeEdgeData extends Record<string, unknown> {
  onAddClick?: () => void;
}

export interface EditFlowSummaryFormValues {
  title: string;
  description: string;
  tag_ids: number[];
}

export enum EditFlowSummaryFormFields {
  TITLE = 'title',
  DESCRIPTION = 'description',
  TAG_IDS = 'tag_ids',
}

export enum RuleFlowStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DRAFT = 'DRAFT',
  VALIDATION = 'VALIDATION',
}

/** Status of a rule flow validation run (from validations endpoint). */
export enum RuleFlowValidationStatus {
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
}

export enum FlowNodeTypes {
  ADD = 'add',
  ENDING = 'ending',
  PLACEHOLDER = 'placeholder',
  RULE = 'rule',
  STARTING = 'starting',
}

// Base interface for common node data properties
interface RuleFlowBaseNodeData {
  readOnly?: boolean;
  openNodeDrawer: (type: NodeDrawerTypes) => void;
}

export type StartingNodeData = Node<
  RuleFlowBaseNodeData & Record<string, unknown>,
  FlowNodeTypes.STARTING
>;

export type EndingNodeData = Node<
  RuleFlowBaseNodeData & Record<string, unknown>,
  FlowNodeTypes.ENDING
>;

export type RuleNodeData = Node<
  RuleFlowBaseNodeData & {
    ruleId: number;
    isFirst?: boolean;
    isLast?: boolean;
    removeRuleFromFlow: (payload: RemoveFlowRulePayload) => void;
    moveNodeInFlow: (direction: MoveDirection) => void;
  } & Record<string, unknown>,
  FlowNodeTypes.RULE
>;

export type PlaceholderNodeData = Node<
  RuleFlowBaseNodeData & Record<string, unknown>,
  FlowNodeTypes.PLACEHOLDER
>;

export type NodeData =
  | StartingNodeData
  | EndingNodeData
  | RuleNodeData
  | PlaceholderNodeData;

export enum MoveDirection {
  UP = 'up',
  DOWN = 'down',
}
export interface RuleAssociation {
  rule_flow_id: number;
  rule_id: number;
  parent_rule_id: number | null;
}

export interface RuleFlowLatestValidation {
  created_at: string;
  exec_range_end: string;
  exec_range_start: string;
  id: number;
  status: RuleFlowValidationStatus;
}

export interface RuleFlow {
  activated_at: string | null;
  actual_created_by: string;
  actual_created_by_id: number;
  archived_at: string | null;
  created_at: string;
  created_by: string;
  created_by_id: number;
  default_alert_queue_id: number | null;
  default_decision: RtrDecision;
  default_generates_alerts: boolean;
  derived_from_id: number | null;
  description: string;
  event_subtypes: string[];
  event_type: EventTypes;
  id: number;
  latest_validation: RuleFlowLatestValidation | null;
  org_id: number;
  rule_associations: RuleAssociation[];
  rules: RuleSummary[];
  status: RuleFlowStatus;
  tags: FullTagResponse[];
  title: string;
  updated_at: string;
}
